import { EditorHelper } from '@getaccept/editor-lib';
import type { Comment, Reply } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { nanoid } from 'nanoid';
import type { CommentParticipant } from '../types/comment-participant';
import type { CommentReadEvent } from '../types/comment-read-event';
import type { CommentEditorSelection } from '../types/comment-selection';
import { CommentStatus } from '../types/comment-status';

export class CommentsFactory {
  static comment({
    id,
    richText,
    text,
    createdBy,
    status,
    pageId,
    editorSelection,
    replies,
    reads,
    createdAt,
    updatedAt,
    isTemporary,
  }: Partial<Comment> & { createdBy: CommentParticipant }): Comment {
    return {
      id: id || nanoid(EditorHelper.NANO_ID_LENGTH),
      richText: richText || '',
      text: text || '',
      createdBy: CommentsFactory.commentParticipant(createdBy),
      status: status || CommentStatus.Draft,
      pageId,
      editorSelection,
      replies: replies || [],
      reads: reads || [],
      createdAt: createdAt || Date.now(),
      updatedAt: updatedAt || Date.now(),
      isTemporary: isTemporary || undefined,
    };
  }

  static editorSelection({
    editorBlockId,
    sectionId,
    rowId,
    cellId,
    nodeId,
    textSelection,
    content,
    detachedFromContent,
    editorBlockVersion,
  }: Partial<CommentEditorSelection> = {}): CommentEditorSelection {
    return {
      editorBlockId: editorBlockId || nanoid(EditorHelper.NANO_ID_LENGTH),
      sectionId,
      rowId,
      cellId,
      nodeId,
      textSelection,
      content,
      detachedFromContent,
      editorBlockVersion,
    };
  }

  static commentParticipant({
    userId,
    recipientId,
  }: Partial<CommentParticipant>): CommentParticipant {
    if (userId) {
      return { userId, recipientId: null };
    }

    if (recipientId) {
      return { recipientId, userId: null };
    }

    throw Error('userId or recipientId must be provided');
  }

  static reply({
    id,
    richText,
    text,
    commentId,
    status,
    reads,
    createdBy,
    createdAt,
    updatedAt,
  }: Partial<Reply> & { createdBy: CommentParticipant }): Reply {
    return {
      id: id || nanoid(EditorHelper.NANO_ID_LENGTH),
      richText: richText || '',
      text: text || '',
      commentId: commentId || nanoid(EditorHelper.NANO_ID_LENGTH),
      status: status || CommentStatus.Draft,
      reads: reads || [],
      createdBy: CommentsFactory.commentParticipant(createdBy),
      createdAt: createdAt || Date.now(),
      updatedAt: updatedAt || Date.now(),
    };
  }

  static readEvent({
    participant,
    timestamp,
  }: Partial<CommentReadEvent> & { participant: CommentParticipant }): CommentReadEvent {
    return {
      participant: CommentsFactory.commentParticipant(participant),
      timestamp: timestamp || Date.now(),
    };
  }
}
